import React from "react";
import { COMPANY, CERTS, STATS } from "../data/site";
import { CheckCircle2, ArrowRight, Sparkles } from "lucide-react";
import useReveal from "../hooks/useReveal";

const About = () => {
  const [ref, inView] = useReveal();
  return (
    <section
      id="about"
      ref={ref}
      className={`relative py-28 bg-[#fffaf3] overflow-hidden reveal ${inView ? "in-view" : ""}`}
      data-testid="about-section"
    >
      {/* dotted grid bg */}
      <div className="absolute inset-0 dot-grid opacity-30 pointer-events-none" />
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-orange-300/30 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-14 items-start relative">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-[10px] font-extrabold tracking-[0.25em] uppercase font-mono mb-6">
            <Sparkles className="w-3 h-3" /> ISO 9001:2015 Certified
          </div>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold text-[#1a0a05] leading-[0.95] mb-8 tracking-tight">
            Welcome to <span className="text-heat brush">Ess Bee<br />Heaters.</span>
          </h2>
          <p className="text-[#3a2418] leading-relaxed mb-5 text-base sm:text-lg max-w-2xl">{COMPANY.aboutLong}</p>
          <p className="text-[#3a2418]/75 leading-relaxed mb-8 text-sm sm:text-base max-w-2xl">{COMPANY.aboutLong2}</p>

          <div className="grid sm:grid-cols-2 gap-3 mb-9 max-w-xl">
            {["ISO 9001:2015 Certified", "RoHS Compliant", "CE Certified", "Quality Tested"].map((c) => (
              <div
                key={c}
                className="flex items-center gap-2.5 text-sm font-semibold text-[#1a0a05] bg-white px-4 py-3 rounded-xl border border-orange-100"
              >
                <CheckCircle2 className="w-4 h-4 text-orange-600 shrink-0" /> {c}
              </div>
            ))}
          </div>

          <a
            href="#products"
            data-testid="about-explore-btn"
            className="group inline-flex items-center gap-2 heat-gradient text-white px-7 py-3.5 rounded-full font-bold shadow-xl shadow-red-500/30 hover:scale-[1.03] transition-transform"
          >
            Explore Products{" "}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>

        {/* Right column — asymmetric stats stack */}
        <div className="lg:col-span-5 space-y-4">
          {STATS.map((s, i) => (
            <div
              key={s.label}
              className={`gradient-border lift p-6 ${i % 2 === 1 ? "ml-auto" : ""}`}
              style={{ maxWidth: i % 2 === 1 ? "90%" : "100%" }}
              data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="flex items-baseline gap-3">
                <div className="font-display font-extrabold text-5xl sm:text-6xl text-heat kpi-num leading-none">
                  {s.value}
                </div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#3a2418]/70 font-mono">
                  {s.label}
                </div>
              </div>
            </div>
          ))}

          {/* Certifications card */}
          <div className="relative bg-[#1a0a05] text-amber-50 rounded-2xl p-7 overflow-hidden">
            <div className="absolute -top-12 -right-12 w-48 h-48 bg-orange-500/25 blur-3xl rounded-full" />
            <div className="relative">
              <div className="text-[10px] tracking-[0.3em] text-amber-300 font-bold mb-4 font-mono uppercase">Trusted Quality</div>
              <h3 className="font-display text-2xl font-extrabold mb-5">Our Certifications</h3>
              <div className="grid grid-cols-2 gap-3">
                {CERTS.map((c) => (
                  <div
                    key={c.title}
                    className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 hover:border-orange-400/60 transition-colors"
                  >
                    <div className="font-display font-bold text-orange-300 text-sm">{c.title}</div>
                    <div className="text-[10px] text-amber-100/70 uppercase tracking-[0.18em] mt-1 font-mono">{c.sub}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
